/**
 * TagStore — sl-xbxb, sl-o9xj
 *
 * Dual data-source store for workspace tag definitions.
 * - Local workspaces: Y.js rootYDoc.meta.properties.tags.options (read + write)
 * - Cloud workspaces: Y.js (offline buffer + write) + GraphQL (server truth + DB)
 *
 * Local-First guarantee: feature-enabled writes go to Y.js regardless of workspace type.
 * For cloud workspaces, writes also fire GraphQL mutations (fast-path to DB)
 * and update cloudTags$ (optimistic UI).
 *
 * Offline-Resilience (sl-o9xj): Failed GraphQL mutations are queued and
 * retried on reconnect. Follows AccessibleDocCacheStore pattern:
 * - Reconnect listener (window 'online' event)
 * - Visibility listener (document 'visibilitychange')
 * - Targeted workspace metadata invalidations
 *
 * SOLID: S — Tag data access only
 * SOLID: D — Depends on WorkspaceService + WorkspaceServerService abstractions
 */

// Direct import to avoid circular dependency (tag → cloud → workspace → tag)
import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import {
  isBackendKnownUnavailable,
  markBackendAvailable,
  markBackendUnavailable,
  onBackendAvailable,
} from '@affine/core/utils/backend-availability';
import {
  createTagMutation,
  deleteTagMutation,
  docTagIdsQuery,
  tagDocMutation,
  untagDocMutation,
  updateTagMutation,
  workspaceTagsQuery,
} from '@affine/graphql';
import type { DocsPropertiesMeta } from '@blocksuite/affine/store';
import {
  LiveData,
  Store,
  yjsGetPath,
  yjsObserveDeep,
  yjsObservePath,
} from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { map, type Observable, switchMap } from 'rxjs';
import { Array as YArray } from 'yjs';

import {
  FEATURE_KEY_TAGS,
  isProductFeatureAllowed,
} from '../../streamlain/hooks/product-feature-gates';
import type { WorkspaceService } from '../../workspace';
import type { WorkspaceMetadataChange } from '../../workspace-projection-reconciliation/types';

// ─── Offline Mutation Queue Types (sl-o9xj.1.2) ────────────────────

type PendingMutation =
  | { type: 'createTag'; id: string; name: string; color: string }
  | { type: 'updateTag'; id: string; name: string | null; color: string | null }
  | { type: 'deleteTag'; id: string }
  | { type: 'tagDoc'; docId: string; tagId: string }
  | { type: 'untagDoc'; docId: string; tagId: string };

export type Tag = {
  value: string;
  id: string;
  color: string;
  createDate?: number | Date | undefined;
  updateDate?: number | Date | undefined;
  parentId?: string | undefined;
};

export class TagStore extends Store {
  get properties() {
    return this.workspaceService.workspace.docCollection.meta.properties;
  }

  private get isCloudWorkspace(): boolean {
    return this.workspaceService.workspace.flavour !== 'local';
  }

  // ─── Cloud tag cache ────────────────────────────────────────

  /**
   * Cached tag definitions fetched from GraphQL.
   * Only populated for cloud workspaces.
   */
  private readonly cloudTags$ = new LiveData<Tag[]>([]);

  /**
   * Cached doc-tag mappings fetched from GraphQL.
   * Map<tagId, docId[]> — inverted from the server's Map<docId, tagId[]>.
   * Only populated for cloud workspaces.
   */
  private readonly cloudDocTagsByTagId$ = new LiveData<Map<string, string[]>>(
    new Map()
  );

  /**
   * Cached doc-tag mappings fetched from GraphQL.
   * Array<{ id: string; tags: string[] }> — same shape as _yjsAllDocTagIds().
   * Only populated for cloud workspaces.
   */
  private readonly cloudDocTags$ = new LiveData<
    Array<{ id: string; tags: string[] }>
  >([]);

  private cloudFetchTriggered = false;

  // ─── Offline mutation queue (sl-o9xj.1.2) ──────────────────
  private readonly pendingMutations: PendingMutation[] = [];

  // ─── Listener cleanup (sl-o9xj.1.1) ───────────────────────
  private readonly disposeListeners: (() => void)[] = [];

  // ─── Reactive tag options (dual-path) ──────────────────────

  tagOptions$ = this.isCloudWorkspace
    ? this.cloudTags$
    : LiveData.from(
        yjsGetPath(
          this.workspaceService.workspace.rootYDoc.getMap('meta'),
          'properties.tags.options'
        ).pipe(
          switchMap(yjsObserveDeep),
          map(tagOptions => {
            if (tagOptions instanceof YArray) {
              return tagOptions.toJSON();
            } else {
              return [];
            }
          })
        ),
        []
      );

  subscribe(cb: () => void) {
    const disposable =
      this.workspaceService.workspace.docCollection.slots.docListUpdated.subscribe(
        cb
      );
    return disposable.unsubscribe.bind(disposable);
  }

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super();

    if (this.isCloudWorkspace) {
      // Seed cloud cache from Y.js immediately (offline-resilient initial state).
      // This ensures tags are visible even if the GraphQL fetch fails (offline).
      this._seedCloudCacheFromYjs();
      // Then fetch from server (overwrites Y.js seed with server truth when online)
      this.ensureCloudTagsFetched();
      // Catch up after reconnect/focus; live changes arrive through the
      // workspace projection reconciler.
      this.startReconnectListener();
      this.startVisibilityListener();
    }
  }

  private isTagsFeatureEnabled(): boolean {
    return isProductFeatureAllowed(FEATURE_KEY_TAGS);
  }

  private assertTagsFeatureEnabled(): void {
    if (!this.isTagsFeatureEnabled()) {
      throw new Error('Tags feature is disabled');
    }
  }

  /**
   * Clean up listeners. Called when the store is disposed.
   */
  override dispose(): void {
    for (const dispose of this.disposeListeners) {
      dispose();
    }
    this.disposeListeners.length = 0;
    this.pendingMutations.length = 0;
    super.dispose();
  }

  // ─── Cloud data fetching ───────────────────────────────────

  /**
   * Fetch tag definitions and doc-tag mappings from GraphQL.
   * Only for cloud workspaces. Idempotent — skips if already triggered.
   */
  ensureCloudTagsFetched(): void {
    if (this.cloudFetchTriggered || !this.isCloudWorkspace) return;
    if (isBackendKnownUnavailable()) return;
    this.cloudFetchTriggered = true;

    this.fetchCloudTags().catch(error => {
      markBackendUnavailable(error);
      // Retry once after a short delay (server may not be bound yet)
      this.cloudFetchTriggered = false;
      setTimeout(() => {
        if (isBackendKnownUnavailable()) return;
        if (!this.cloudFetchTriggered) {
          this.cloudFetchTriggered = true;
          this.fetchCloudTags().catch(error => {
            markBackendUnavailable(error);
            // Unblock with empty state — online listener will update later
            this.cloudFetchTriggered = false;
          });
        }
      }, 500);
    });
  }

  private async fetchCloudTags(): Promise<void> {
    await Promise.all([
      this.fetchCloudTagDefinitions(),
      this.fetchCloudDocTagMappings(),
    ]);
    markBackendAvailable();
  }

  private async fetchCloudTagDefinitions(): Promise<void> {
    const server = this.workspaceServerService.server;
    if (!server) throw new Error('Server not bound');
    const workspaceId = this.workspaceService.workspace.id;
    const result = await server.gql({
      query: workspaceTagsQuery,
      variables: { workspaceId },
    });
    const tags: Tag[] = result.workspaceTags.map(t => ({
      id: t.id,
      value: t.name,
      color: t.color,
      createDate: new Date(t.createdAt).getTime(),
      updateDate: new Date(t.updatedAt).getTime(),
    }));
    this.cloudTags$.next(tags);
    markBackendAvailable();
  }

  private async fetchCloudDocTagMappings(): Promise<void> {
    const server = this.workspaceServerService.server;
    if (!server) throw new Error('Server not bound');
    const workspaceId = this.workspaceService.workspace.id;
    const result = await server.gql({
      query: docTagIdsQuery,
      variables: { workspaceId },
    });
    const byTagId = new Map<string, string[]>();
    const docTags: Array<{ id: string; tags: string[] }> = [];

    for (const entry of result.docTagIds) {
      docTags.push({ id: entry.docId, tags: entry.tagIds });
      for (const tagId of entry.tagIds) {
        const existing = byTagId.get(tagId);
        if (existing) {
          existing.push(entry.docId);
        } else {
          byTagId.set(tagId, [entry.docId]);
        }
      }
    }

    this.cloudDocTagsByTagId$.next(byTagId);
    this.cloudDocTags$.next(docTags);
    markBackendAvailable();
  }

  /**
   * Refresh cloud tag data from server.
   * Called after mutations (create/update/delete tag, tag/untag doc).
   */
  async refreshCloudTags(): Promise<void> {
    this.cloudFetchTriggered = false;
    this.ensureCloudTagsFetched();
  }

  reconcileMetadataChange(event: WorkspaceMetadataChange): void {
    if (event.workspaceId !== this.workspaceService.workspace.id) return;
    if (
      event.changeType === 'tag_created' ||
      event.changeType === 'tag_updated'
    ) {
      void this.fetchCloudTagDefinitions().catch(markBackendUnavailable);
      return;
    }
    if (event.changeType === 'tag_deleted') {
      void Promise.all([
        this.fetchCloudTagDefinitions(),
        this.fetchCloudDocTagMappings(),
      ]).catch(markBackendUnavailable);
      return;
    }
    if (event.changeType === 'doc_tags_updated') {
      void this.fetchCloudDocTagMappings().catch(markBackendUnavailable);
    }
  }

  // ─── Read API (consumed by TagList, Tag entities) ──────────

  watchTagIds(): Observable<string[]> {
    return this.tagOptions$.map(tags => tags.map(tag => tag.id)).asObservable();
  }

  watchTagInfo(id: string): Observable<Tag | undefined> {
    return this.tagOptions$.map(
      tags => tags.find(tag => tag.id === id) as Tag | undefined
    );
  }

  watchTagPageIds(id: string): Observable<string[]> {
    if (this.isCloudWorkspace) {
      return this.cloudDocTagsByTagId$
        .map(byTagId => byTagId.get(id) ?? [])
        .asObservable();
    }

    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => {
        return yjsObservePath(pages, '*.tags');
      }),
      map(meta => {
        if (meta instanceof YArray) {
          return meta
            .map(v => {
              const tags = v.get('tags') as YArray<string> | undefined;
              if (tags instanceof YArray) {
                for (const tagId of tags.toArray()) {
                  if (tagId === id) {
                    return v.get('id') as string;
                  }
                }
              }
              return null;
            })
            .filter(Boolean) as string[];
        } else {
          return [];
        }
      })
    );
  }

  /**
   * Watch all doc-tag mappings. Used by DocsStore.watchAllDocTagIds().
   * Returns Array<{ id: string; tags: string[] }>.
   */
  watchAllDocTagIds(): Observable<Array<{ id: string; tags: string[] }>> {
    if (this.isCloudWorkspace) {
      return this.cloudDocTags$.asObservable();
    }
    return this._yjsAllDocTagIds();
  }

  private _yjsAllDocTagIds(): Observable<
    Array<{ id: string; tags: string[] }>
  > {
    return yjsGetPath(
      this.workspaceService.workspace.rootYDoc.getMap('meta'),
      'pages'
    ).pipe(
      switchMap(pages => yjsObservePath(pages, '*.tags')),
      map(pages => {
        if (pages instanceof YArray) {
          return pages.map(v => ({
            id: v.get('id') as string,
            tags: (() => {
              const tags = v.get('tags');
              if (tags instanceof YArray) {
                return tags.toJSON() as string[];
              }
              return (tags ?? []) as string[];
            })(),
          }));
        } else {
          return [];
        }
      })
    );
  }

  // ─── Write API (feature-gated, then dual-path Y.js local / GraphQL cloud) ────

  createNewTag(value: string, color: string): string {
    this.assertTagsFeatureEnabled();

    const newId = nanoid();
    const now = Date.now();
    const newTag: Tag = {
      id: newId,
      value,
      color,
      createDate: now,
      updateDate: now,
    };

    // Write to Y.js for offline persistence and sync buffering.
    this._yjsUpdateTagOptions([...this._currentYjsTagOptions(), newTag]);

    if (this.isCloudWorkspace) {
      // Optimistic update for immediate UI (cloudTags$ is the read source)
      this.cloudTags$.next([...this.cloudTags$.value, newTag]);
      // Fast-path: persist to DB via GraphQL (fire-and-forget)
      this.cloudCreateTag(newId, value, color);
    }

    return newId;
  }

  deleteTag(id: string): void {
    this.assertTagsFeatureEnabled();

    // Write to Y.js for offline persistence and sync buffering.
    this._yjsRemoveTagOption(id);

    if (this.isCloudWorkspace) {
      // Optimistic update
      this.cloudTags$.next(this.cloudTags$.value.filter(t => t.id !== id));
      this.cloudDeleteTag(id);
    }
  }

  updateTagInfo(id: string, tagInfo: Partial<Tag>): void {
    this.assertTagsFeatureEnabled();

    // Write to Y.js for offline persistence and sync buffering.
    const tag = (
      this.isCloudWorkspace
        ? this.cloudTags$.value.find(t => t.id === id)
        : this.tagOptions$.value.find(t => t.id === id)
    ) as Tag | undefined;
    if (!tag) return;

    const updated: Tag = {
      id,
      value: tag.value,
      color: tag.color,
      createDate: tag.createDate,
      updateDate: Date.now(),
      ...tagInfo,
    };
    this._yjsUpdateTagOption(id, updated);

    if (this.isCloudWorkspace) {
      // Optimistic update
      this.cloudTags$.next(
        this.cloudTags$.value.map(t =>
          t.id === id ? { ...t, ...tagInfo, updateDate: Date.now() } : t
        )
      );
      this.cloudUpdateTag(id, tagInfo);
    }
  }

  /**
   * Tag a doc. Writes to Y.js after the feature gate passes.
   * For cloud workspaces, also fires GraphQL mutation + optimistic cache update.
   */
  tagDoc(docId: string, tagId: string): void {
    this.assertTagsFeatureEnabled();

    // Write to Y.js for offline persistence and sync buffering.
    const currentTags = this._getYjsDocTags(docId);
    if (!currentTags.includes(tagId)) {
      this._yjsUpdatePageTags(docId, [...currentTags, tagId]);
    }

    if (this.isCloudWorkspace) {
      this._cloudAddDocTag(docId, tagId);
      this.cloudTagDoc(docId, tagId);
    }
  }

  /**
   * Untag a doc. Writes to Y.js after the feature gate passes.
   * For cloud workspaces, also fires GraphQL mutation + optimistic cache update.
   */
  untagDoc(docId: string, tagId: string): void {
    this.assertTagsFeatureEnabled();

    // Write to Y.js for offline persistence and sync buffering.
    const currentTags = this._getYjsDocTags(docId);
    this._yjsUpdatePageTags(
      docId,
      currentTags.filter(t => t !== tagId)
    );

    if (this.isCloudWorkspace) {
      this._cloudRemoveDocTag(docId, tagId);
      this.cloudUntagDoc(docId, tagId);
    }
  }

  // ─── Cloud write operations ──────────────────────────────

  private cloudCreateTag(id: string, name: string, color: string): void {
    const server = this.workspaceServerService.server;
    if (!server || isBackendKnownUnavailable()) {
      this.queueMutation({ type: 'createTag', id, name, color });
      return;
    }
    const workspaceId = this.workspaceService.workspace.id;
    server
      .gql({
        query: createTagMutation,
        variables: { workspaceId, id, name, color },
      })
      .then(() => this.refreshCloudTags())
      .catch(error => {
        markBackendUnavailable(error);
        this.queueMutation({ type: 'createTag', id, name, color });
      });
  }

  private cloudUpdateTag(id: string, tagInfo: Partial<Tag>): void {
    const server = this.workspaceServerService.server;
    const name = tagInfo.value ?? null;
    const color = tagInfo.color ?? null;
    if (!server || isBackendKnownUnavailable()) {
      this.queueMutation({ type: 'updateTag', id, name, color });
      return;
    }
    const workspaceId = this.workspaceService.workspace.id;
    server
      .gql({
        query: updateTagMutation,
        variables: { workspaceId, tagId: id, name, color },
      })
      .then(() => this.refreshCloudTags())
      .catch(error => {
        markBackendUnavailable(error);
        this.queueMutation({ type: 'updateTag', id, name, color });
      });
  }

  private cloudDeleteTag(id: string): void {
    const server = this.workspaceServerService.server;
    if (!server || isBackendKnownUnavailable()) {
      this.queueMutation({ type: 'deleteTag', id });
      return;
    }
    const workspaceId = this.workspaceService.workspace.id;
    server
      .gql({
        query: deleteTagMutation,
        variables: { workspaceId, tagId: id },
      })
      .then(() => this.refreshCloudTags())
      .catch(error => {
        markBackendUnavailable(error);
        this.queueMutation({ type: 'deleteTag', id });
      });
  }

  private cloudTagDoc(docId: string, tagId: string): void {
    const server = this.workspaceServerService.server;
    if (!server || isBackendKnownUnavailable()) {
      this.queueMutation({ type: 'tagDoc', docId, tagId });
      return;
    }
    const workspaceId = this.workspaceService.workspace.id;
    server
      .gql({
        query: tagDocMutation,
        variables: { workspaceId, docId, tagId },
      })
      .then(() => this.refreshCloudTags())
      .catch(error => {
        markBackendUnavailable(error);
        this.queueMutation({ type: 'tagDoc', docId, tagId });
      });
  }

  private cloudUntagDoc(docId: string, tagId: string): void {
    const server = this.workspaceServerService.server;
    if (!server || isBackendKnownUnavailable()) {
      this.queueMutation({ type: 'untagDoc', docId, tagId });
      return;
    }
    const workspaceId = this.workspaceService.workspace.id;
    server
      .gql({
        query: untagDocMutation,
        variables: { workspaceId, docId, tagId },
      })
      .then(() => this.refreshCloudTags())
      .catch(error => {
        markBackendUnavailable(error);
        this.queueMutation({ type: 'untagDoc', docId, tagId });
      });
  }

  private _cloudAddDocTag(docId: string, tagId: string): void {
    const current = this.cloudDocTags$.value;
    const entry = current.find(e => e.id === docId);
    if (entry) {
      if (!entry.tags.includes(tagId)) {
        this.cloudDocTags$.next(
          current.map(e =>
            e.id === docId ? { ...e, tags: [...e.tags, tagId] } : e
          )
        );
      }
    } else {
      this.cloudDocTags$.next([...current, { id: docId, tags: [tagId] }]);
    }

    // Update inverted index
    const byTagId = new Map(this.cloudDocTagsByTagId$.value);
    const existing = byTagId.get(tagId);
    if (existing) {
      if (!existing.includes(docId)) {
        byTagId.set(tagId, [...existing, docId]);
      }
    } else {
      byTagId.set(tagId, [docId]);
    }
    this.cloudDocTagsByTagId$.next(byTagId);
  }

  private _cloudRemoveDocTag(docId: string, tagId: string): void {
    this.cloudDocTags$.next(
      this.cloudDocTags$.value.map(e =>
        e.id === docId ? { ...e, tags: e.tags.filter(t => t !== tagId) } : e
      )
    );

    // Update inverted index
    const byTagId = new Map(this.cloudDocTagsByTagId$.value);
    const existing = byTagId.get(tagId);
    if (existing) {
      byTagId.set(
        tagId,
        existing.filter(d => d !== docId)
      );
    }
    this.cloudDocTagsByTagId$.next(byTagId);
  }

  // ─── Y.js seed + sync helpers ───────────────────────────────

  /**
   * Seed cloudTags$ from Y.js data on construction.
   * Provides immediate tag visibility even when GraphQL is unavailable (offline).
   * Server fetch will overwrite this with authoritative data when online.
   */
  private _seedCloudCacheFromYjs(): void {
    const yjsTags = this._currentYjsTagOptions();
    if (yjsTags.length > 0) {
      this.cloudTags$.next(yjsTags);
    }

    // Also seed doc-tag mappings from Y.js
    const docMetas =
      this.workspaceService.workspace.docCollection.meta.docMetas;
    const docTags: Array<{ id: string; tags: string[] }> = [];
    const byTagId = new Map<string, string[]>();

    for (const meta of docMetas) {
      const tags = meta.tags ?? [];
      if (tags.length > 0) {
        docTags.push({ id: meta.id, tags });
        for (const tagId of tags) {
          const existing = byTagId.get(tagId);
          if (existing) {
            existing.push(meta.id);
          } else {
            byTagId.set(tagId, [meta.id]);
          }
        }
      }
    }

    if (docTags.length > 0) {
      this.cloudDocTags$.next(docTags);
      this.cloudDocTagsByTagId$.next(byTagId);
    }
  }

  /**
   * Read current Y.js tag options synchronously.
   * Used by write methods that need current state before updating.
   */
  private _currentYjsTagOptions(): Tag[] {
    return (this.properties?.tags?.options ?? []) as Tag[];
  }

  // ─── Y.js write operations ─────────────────────────────────

  private _yjsUpdateProperties(properties: DocsPropertiesMeta): void {
    this.workspaceService.workspace.docCollection.meta.setProperties(
      properties
    );
  }

  private _yjsUpdateTagOptions(options: Tag[]): void {
    this._yjsUpdateProperties({
      ...this.properties,
      tags: {
        options,
      },
    });
  }

  private _yjsUpdateTagOption(id: string, option: Tag): void {
    this._yjsUpdateTagOptions(
      this.tagOptions$.value.map(o => (o.id === id ? option : o))
    );
  }

  private _yjsRemoveTagOption(id: string): void {
    this.workspaceService.workspace.docCollection.doc.transact(() => {
      this._yjsUpdateTagOptions(
        this.tagOptions$.value.filter(o => o.id !== id)
      );
      // Remove tag from all pages
      this.workspaceService.workspace.docCollection.docs.forEach(doc => {
        const tags = doc.meta?.tags ?? [];
        if (tags.includes(id)) {
          this._yjsUpdatePageTags(
            doc.id,
            tags.filter(t => t !== id)
          );
        }
      });
    });
  }

  private _yjsUpdatePageTags(pageId: string, tags: string[]): void {
    this.workspaceService.workspace.docCollection.meta.setDocMeta(pageId, {
      tags,
    });
  }

  private _getYjsDocTags(docId: string): string[] {
    const docMeta =
      this.workspaceService.workspace.docCollection.meta.getDocMeta(docId);
    return docMeta?.tags ?? [];
  }

  // ─── Reconnect / Visibility Listeners (sl-o9xj.1.1) ───

  private reconnectDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Listen for network reconnection. After going back online, flush
   * pending offline mutations and refresh the cache from server.
   * Debounced by 1s to avoid rapid-fire on flaky connections.
   */
  private startReconnectListener(): void {
    const handler = () => {
      if (this.reconnectDebounceTimer) {
        clearTimeout(this.reconnectDebounceTimer);
      }
      this.reconnectDebounceTimer = setTimeout(() => {
        this.reconnectDebounceTimer = null;
        void this.flushPendingMutations();
      }, 1000);
    };
    window.addEventListener('online', handler);
    const disposeBackendAvailable = onBackendAvailable(() => {
      handler();
    });
    this.disposeListeners.push(() => {
      window.removeEventListener('online', handler);
      disposeBackendAvailable();
      if (this.reconnectDebounceTimer) {
        clearTimeout(this.reconnectDebounceTimer);
      }
    });
  }

  /**
   * Listen for tab visibility changes. When the user switches back to
   * this tab, refresh tags from server (cross-client sync).
   */
  private startVisibilityListener(): void {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        if (isBackendKnownUnavailable()) return;
        void this.refreshCloudTags();
      }
    };
    document.addEventListener('visibilitychange', handler);
    this.disposeListeners.push(() => {
      document.removeEventListener('visibilitychange', handler);
    });
  }

  // ─── Offline Mutation Queue (sl-o9xj.1.2) ─────────────────────────

  /**
   * Queue a mutation that failed due to network error.
   * Deduplicates by checking if an equivalent mutation is already queued.
   */
  private queueMutation(mutation: PendingMutation): void {
    if (!this.isTagsFeatureEnabled()) {
      return;
    }

    // Deduplicate: don't queue the same create/delete/update twice
    const isDuplicate = this.pendingMutations.some(m => {
      if (m.type !== mutation.type) return false;
      switch (m.type) {
        case 'createTag':
        case 'deleteTag':
        case 'updateTag':
          return m.id === (mutation as typeof m).id;
        case 'tagDoc':
        case 'untagDoc':
          return (
            m.docId === (mutation as typeof m).docId &&
            m.tagId === (mutation as typeof m).tagId
          );
        default:
          return false;
      }
    });
    if (!isDuplicate) {
      this.pendingMutations.push(mutation);
    }
  }

  /**
   * Flush all pending offline mutations, then refresh from server.
   * Called on reconnect (window 'online' event).
   */
  private async flushPendingMutations(): Promise<void> {
    const server = this.workspaceServerService.server;
    if (!server) return;
    if (isBackendKnownUnavailable()) return;
    if (!this.isTagsFeatureEnabled()) {
      this.pendingMutations.length = 0;
      return;
    }

    const mutations = [...this.pendingMutations];
    this.pendingMutations.length = 0;

    const workspaceId = this.workspaceService.workspace.id;

    for (const mutation of mutations) {
      try {
        switch (mutation.type) {
          case 'createTag':
            await server.gql({
              query: createTagMutation,
              variables: {
                workspaceId,
                id: mutation.id,
                name: mutation.name,
                color: mutation.color,
              },
            });
            break;
          case 'updateTag':
            await server.gql({
              query: updateTagMutation,
              variables: {
                workspaceId,
                tagId: mutation.id,
                name: mutation.name,
                color: mutation.color,
              },
            });
            break;
          case 'deleteTag':
            await server.gql({
              query: deleteTagMutation,
              variables: { workspaceId, tagId: mutation.id },
            });
            break;
          case 'tagDoc':
            await server.gql({
              query: tagDocMutation,
              variables: {
                workspaceId,
                docId: mutation.docId,
                tagId: mutation.tagId,
              },
            });
            break;
          case 'untagDoc':
            await server.gql({
              query: untagDocMutation,
              variables: {
                workspaceId,
                docId: mutation.docId,
                tagId: mutation.tagId,
              },
            });
            break;
        }
      } catch (error) {
        markBackendUnavailable(error);
        // Re-queue if still offline
        this.queueMutation(mutation);
      }
    }

    // Always refresh from server after flushing
    await this.refreshCloudTags();
  }
}
